import {
  type ReactElement,
  useReducer,
  useMemo,
  useContext,
  createContext,
  createElement,
  useRef,
} from 'react';
import type React from 'react';
import { type Computed, useComputed } from './computed';
import { type Mutation, reducer } from './mutations';
import { type State } from './types';

interface LoadedRefs {
  all: React.MutableRefObject<boolean>;
  campus: React.MutableRefObject<Set<string>>;
}

export interface GroupsStore {
  state: State;
  dispatch: React.Dispatch<Mutation>;
  computed: Computed;
  loaded: LoadedRefs;
}

const GroupsContext = createContext<GroupsStore>({} as GroupsStore);

export function GroupsStoreProvider({ children }: { children: ReactElement }) {
  const [state, dispatch] = useReducer(reducer, {
    data: [],
    allLoaded: false,
    campusLoaded: new Set<string>(),
  });
  const computed = useComputed(state);
  const loadedAll = useRef(false);
  const loadedCampus = useRef(new Set<string>());
  const loaded: LoadedRefs = useMemo(
    () => ({
      all: loadedAll,
      campus: loadedCampus,
    }),
    [loadedAll, loadedCampus],
  );
  const props = useMemo(
    () => ({ state, dispatch, computed, loaded }),
    [state, dispatch, computed, loaded],
  );

  return createElement(GroupsContext.Provider, { value: props }, children);
}

export function useGroupsStore() {
  return useContext(GroupsContext);
}
